/**
 * GraphQL Admin User Mutation Hooks
 *
 * Hooks for admin user management operations.
 */

import { useMutation, gql } from '@apollo/client';
import logger from '../../utils/logger';

// GraphQL Mutations
const ADMIN_CREATE_USER_MUTATION = gql`
  mutation AdminCreateUser($input: AdminCreateUserInput!) {
    adminCreateUser(input: $input)
  }
`;

const ADMIN_UPDATE_USER_MUTATION = gql`
  mutation AdminUpdateUser($input: AdminUpdateUserInput!) {
    adminUpdateUser(input: $input)
  }
`;

const ADMIN_VERIFY_USER_MUTATION = gql`
  mutation AdminVerifyUser($input: AdminUserIdInput!) {
    adminVerifyUser(input: $input)
  }
`;

const ADMIN_UNVERIFY_USER_MUTATION = gql`
  mutation AdminUnverifyUser($input: AdminUserIdInput!) {
    adminUnverifyUser(input: $input)
  }
`;

const ADMIN_SEND_PASSWORD_RESET_MUTATION = gql`
  mutation AdminSendPasswordReset($input: AdminUserIdInput!) {
    adminSendPasswordReset(input: $input) {
      success
      message
    }
  }
`;

const ADMIN_DELETE_USER_MUTATION = gql`
  mutation AdminDeleteUser($input: AdminUserIdInput!) {
    adminDeleteUser(input: $input) {
      success
      message
    }
  }
`;

const ADMIN_GENERATE_TEST_USER_MUTATION = gql`
  mutation AdminGenerateTestUser($input: AdminGenerateTestUserInput!) {
    adminGenerateTestUser(input: $input)
  }
`;

// Types
interface UserPayload {
  id: string;
  globalId: string;
  username: string;
  email: string;
  slug: string;
  displayName: string;
  emailVerified: boolean;
  isTestUser: boolean;
  isAdmin: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string | null;
}

interface CreateUserPayload {
  user: UserPayload;
  temporaryPassword?: string;
}

interface UpdateUserPayload {
  user: UserPayload;
}

interface SuccessPayload {
  success: boolean;
  message: string;
}

interface TestUserPayload {
  user: UserPayload;
  stats: {
    photosCreated: number;
    collectionsCreated: number;
  };
}

interface CreateUserInput {
  username: string;
  email: string;
  displayName?: string;
  password?: string;
}

interface UpdateUserInput {
  id: string;
  username?: string;
  email?: string;
  displayName?: string;
}

interface TestUserInput {
  preset?: 'empty' | 'photographer' | 'curator';
  custom?: {
    username: string;
    photoCount: number;
    collectionCount: number;
    email?: string;
  };
}

/**
 * Hook for admin creating a new user
 */
export function useAdminCreateUserGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { adminCreateUser: CreateUserPayload },
    { input: CreateUserInput }
  >(ADMIN_CREATE_USER_MUTATION, {
    // Note: TanStack Query cache invalidation is handled by the component
    onCompleted: (data) => {
      logger.info('Admin', 'User created via GraphQL', {
        userId: data.adminCreateUser.user.id,
        username: data.adminCreateUser.user.username,
      });
    },
    onError: (err) => {
      logger.error('Admin', 'Failed to create user', err);
    },
  });

  const createUser = async (input: CreateUserInput) => {
    const result = await mutate({ variables: { input } });
    return result.data?.adminCreateUser;
  };

  return { createUser, loading, error };
}

/**
 * Hook for admin updating a user
 */
export function useAdminUpdateUserGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { adminUpdateUser: UpdateUserPayload },
    { input: UpdateUserInput }
  >(ADMIN_UPDATE_USER_MUTATION, {
    // Note: TanStack Query cache invalidation is handled by the component
    onCompleted: (data) => {
      logger.info('Admin', 'User updated via GraphQL', {
        userId: data.adminUpdateUser.user.id,
      });
    },
    onError: (err) => {
      logger.error('Admin', 'Failed to update user', err);
    },
  });

  const updateUser = async (input: UpdateUserInput) => {
    const result = await mutate({ variables: { input } });
    return result.data?.adminUpdateUser;
  };

  return { updateUser, loading, error };
}

/**
 * Hook for admin verifying a user
 */
export function useAdminVerifyUserGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { adminVerifyUser: UpdateUserPayload },
    { input: { id: string } }
  >(ADMIN_VERIFY_USER_MUTATION, {
    // Note: TanStack Query cache invalidation is handled by the component
    onCompleted: (data) => {
      logger.info('Admin', 'User verified via GraphQL', {
        userId: data.adminVerifyUser.user.id,
      });
    },
    onError: (err) => {
      logger.error('Admin', 'Failed to verify user', err);
    },
  });

  const verifyUser = async (userId: string) => {
    const result = await mutate({ variables: { input: { id: userId } } });
    return result.data?.adminVerifyUser;
  };

  return { verifyUser, loading, error };
}

/**
 * Hook for admin un-verifying a user
 */
export function useAdminUnverifyUserGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { adminUnverifyUser: UpdateUserPayload },
    { input: { id: string } }
  >(ADMIN_UNVERIFY_USER_MUTATION, {
    // Note: TanStack Query cache invalidation is handled by the component
    onCompleted: (data) => {
      logger.info('Admin', 'User unverified via GraphQL', {
        userId: data.adminUnverifyUser.user.id,
      });
    },
    onError: (err) => {
      logger.error('Admin', 'Failed to unverify user', err);
    },
  });

  const unverifyUser = async (userId: string) => {
    const result = await mutate({ variables: { input: { id: userId } } });
    return result.data?.adminUnverifyUser;
  };

  return { unverifyUser, loading, error };
}

/**
 * Hook for admin sending password reset
 */
export function useAdminSendPasswordResetGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { adminSendPasswordReset: SuccessPayload },
    { input: { id: string } }
  >(ADMIN_SEND_PASSWORD_RESET_MUTATION, {
    onCompleted: (data) => {
      logger.info('Admin', 'Password reset sent via GraphQL', {
        success: data.adminSendPasswordReset.success,
      });
    },
    onError: (err) => {
      logger.error('Admin', 'Failed to send password reset', err);
    },
  });

  const sendPasswordReset = async (userId: string) => {
    const result = await mutate({ variables: { input: { id: userId } } });
    return result.data?.adminSendPasswordReset;
  };

  return { sendPasswordReset, loading, error };
}

/**
 * Hook for admin deleting a user
 */
export function useAdminDeleteUserGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { adminDeleteUser: SuccessPayload },
    { input: { id: string } }
  >(ADMIN_DELETE_USER_MUTATION, {
    // Note: TanStack Query cache invalidation is handled by the component
    onCompleted: () => {
      logger.info('Admin', 'User deleted via GraphQL');
    },
    onError: (err) => {
      logger.error('Admin', 'Failed to delete user', err);
    },
  });

  const deleteUser = async (userId: string) => {
    const result = await mutate({ variables: { input: { id: userId } } });
    return result.data?.adminDeleteUser;
  };

  return { deleteUser, loading, error };
}

/**
 * Hook for admin generating a test user
 */
export function useAdminGenerateTestUserGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { adminGenerateTestUser: TestUserPayload },
    { input: TestUserInput }
  >(ADMIN_GENERATE_TEST_USER_MUTATION, {
    // Note: TanStack Query cache invalidation is handled by the component
    onCompleted: (data) => {
      logger.info('Admin', 'Test user generated via GraphQL', {
        userId: data.adminGenerateTestUser.user.id,
        username: data.adminGenerateTestUser.user.username,
      });
    },
    onError: (err) => {
      logger.error('Admin', 'Failed to generate test user', err);
    },
  });

  const generateTestUser = async (input: TestUserInput) => {
    const result = await mutate({ variables: { input } });
    return result.data?.adminGenerateTestUser;
  };

  return { generateTestUser, loading, error };
}
